import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  MagnifyingGlass, X, Truck, User, Package, Building, HandPointing,
  Robot, Warning, Sparkle, ArrowRight,
} from "@phosphor-icons/react";
import { flatNavItems } from "../config/workspaces";

/**
 * Cmd+K Command Palette (M3-D · refreshed Feb 2026).
 *
 * Global keyboard-driven search + jump-to-page. Opens with:
 *   • Ctrl/Cmd+K
 *   • forward-slash on any non-input focus
 *
 * Two data sources merged in one list:
 *   1) Static navigation shortcuts sourced from /config/workspaces.js so
 *      the palette stays in perfect sync with the sidebar + tab bar.
 *   2) Live results from GET /api/search/global?q=… (loads, drivers,
 *      carriers, customers, brokers, prospects).
 *
 * Keyboard: ↑↓ to move · Enter to open · Esc to close.
 */

// Sourced from the workspace map — every page is jumpable, aliases like
// "carrier discovery" / "find carriers" are included via item.aliases.
function buildNavItems(isAdmin) {
  return flatNavItems(isAdmin).map((it) => ({
    title: it.label,
    subtitle: it.workspace,
    href: it.to,
    Icon: it.icon,
    kw: [it.label, it.workspace, ...(it.aliases || [])].join(" ").toLowerCase(),
  }));
}

const KIND_META = {
  loads:      { label: "Loads",     Icon: Package,       tint: "#0EA5E9" },
  drivers:    { label: "Drivers",   Icon: User,          tint: "#22C55E" },
  carriers:   { label: "Carriers",  Icon: Truck,         tint: "#DC2626" },
  customers:  { label: "Customers", Icon: Building,      tint: "#A855F7" },
  brokers:    { label: "Brokers",   Icon: Building,      tint: "#F97316" },
  prospects:  { label: "Prospects", Icon: Robot,         tint: "#EAB308" },
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState({});
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const nav = useNavigate();

  // ── keyboard: open/close ──────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(prev => !prev);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      } else if (!open && e.key === "/" &&
                 !["INPUT","TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 40);
    else { setQ(""); setHits({}); setSel(0); }
  }, [open]);

  // ── debounced server query ────────────────────────────────────────
  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setHits({}); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/search/global?q=${encodeURIComponent(q.trim())}&limit_per_kind=5`);
        setHits(r.data?.hits || {});
      } catch { setHits({}); }
      finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  // ── flat list for keyboard nav ────────────────────────────────────
  const NAV_ITEMS = useMemo(() => buildNavItems(true), []);
  const list = useMemo(() => {
    const items = [];
    // Nav shortcuts first (only if q matches or empty)
    const query = q.trim().toLowerCase();
    const navItems = query
      ? NAV_ITEMS.filter(n => n.title.toLowerCase().includes(query) ||
                              n.kw.includes(query))
      : NAV_ITEMS.slice(0, 8);
    navItems.forEach(n => items.push({ kind: "nav", ...n }));
    // Server hits
    Object.entries(hits).forEach(([kind, arr]) => {
      arr.forEach(it => items.push({ kind, ...it }));
    });
    return items;
  }, [hits, q, NAV_ITEMS]);

  useEffect(() => { setSel(0); }, [q, hits]);

  const activate = useCallback((idx) => {
    const item = list[idx];
    if (!item) return;
    setOpen(false);
    nav(item.href);
  }, [list, nav]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel(s => Math.min(s + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); activate(sel); }
  };

  if (!open) return null;

  const navHits = list.filter(it => it.kind === "nav");
  const serverGrouped = Object.entries(KIND_META).map(([kind, meta]) => ({
    kind, meta, items: hits[kind] || [],
  })).filter(g => g.items.length > 0);

  // Flat index mapping for aria-selected
  let idx = -1;
  const nextIdx = () => (++idx);

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-24 px-4"
         style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
         onClick={() => setOpen(false)}
         data-testid="command-palette-overlay">
      <div className="w-full max-w-2xl rounded-lg overflow-hidden shadow-2xl"
           style={{ background: "#0A0A0A", border: "1px solid #262626" }}
           onClick={e => e.stopPropagation()}
           data-testid="command-palette">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "#171717" }}>
          <MagnifyingGlass size={18} color="#737373" />
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Jump anywhere · search loads, drivers, carriers, customers…"
            className="flex-1 bg-transparent outline-none text-base text-neutral-100 placeholder:text-neutral-600"
            data-testid="command-palette-input" />
          {loading && <Sparkle size={14} weight="fill" color="#DC2626" className="animate-pulse" />}
          <button onClick={() => setOpen(false)}
            className="p-1 rounded hover:bg-neutral-800" style={{ color: "#737373" }}
            data-testid="command-palette-close">
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {list.length === 0 && (
            <div className="p-10 text-center text-sm text-neutral-500">
              {q.trim().length < 2 ? "Type to search or navigate" : "No matches"}
            </div>
          )}

          {navHits.length > 0 && (
            <Section title="Navigation">
              {navHits.map(it => {
                const i = nextIdx();
                return <Row key={`nav-${i}`} item={it} Icon={it.Icon}
                             selected={sel === i}
                             onMouseEnter={() => setSel(i)}
                             onClick={() => activate(i)}
                             testid={`command-nav-${i}`} />;
              })}
            </Section>
          )}

          {serverGrouped.map(g => (
            <Section key={g.kind} title={g.meta.label} tint={g.meta.tint}>
              {g.items.map(it => {
                const i = nextIdx();
                return <Row key={`${g.kind}-${it.id}`} item={it} Icon={g.meta.Icon}
                             tint={g.meta.tint} selected={sel === i}
                             onMouseEnter={() => setSel(i)}
                             onClick={() => activate(i)}
                             testid={`command-${g.kind}-${it.id}`} />;
              })}
            </Section>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t flex items-center gap-4 text-[10px] uppercase tracking-widest font-bold"
          style={{ borderColor: "#171717", color: "#525252" }}>
          <span>↑↓ move</span>
          <span>↵ open</span>
          <span>esc close</span>
          <div className="flex-1" />
          <span>Cmd+K</span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, tint, children }) {
  return (
    <div className="py-1">
      <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold"
        style={{ color: tint || "#525252" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ item, Icon, tint = "#A3A3A3", selected, onClick, onMouseEnter, testid }) {
  return (
    <button onClick={onClick} onMouseEnter={onMouseEnter} data-testid={testid}
      className="w-full flex items-center gap-3 px-4 py-2 text-left"
      style={{ background: selected ? "#171717" : "transparent",
                borderLeft: `2px solid ${selected ? tint : "transparent"}` }}>
      <Icon size={14} weight="fill" color={tint} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-neutral-100 truncate">{item.title}</div>
        {item.subtitle && (
          <div className="text-[11px] text-neutral-500 truncate">{item.subtitle}</div>
        )}
      </div>
      {item.meta && (
        <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
          style={{ background: "#171717", color: "#A3A3A3" }}>
          {item.meta}
        </span>
      )}
      {selected && <ArrowRight size={12} weight="bold" color={tint} />}
    </button>
  );
}
